import { useState, useEffect, useRef } from 'react';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  HelpCircle,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ScreeningProgress as ScreeningProgressType } from '@/features/evaluation/types';

interface ScreeningProgressProps {
  progress: ScreeningProgressType;
  onCancel: () => void;
  onComplete: () => void;
}

export function ScreeningProgress({
  progress,
  onCancel,
  onComplete,
}: ScreeningProgressProps) {
  const [eta, setEta] = useState<string>('Calculating...');
  const startTimeRef = useRef(Date.now());

  const percent =
    progress.total_sources > 0
      ? (progress.completed_sources / progress.total_sources) * 100
      : 0;

  const isComplete =
    progress.completed_batches + progress.failed_batches >= progress.total_batches &&
    progress.total_batches > 0;

  const includeCount = progress.results.filter((r) => r.verdict === 'include').length;
  const maybeCount = progress.results.filter((r) => r.verdict === 'maybe').length;
  const excludeCount = progress.results.filter((r) => r.verdict === 'exclude').length;

  // Calculate ETA
  useEffect(() => {
    const processed = progress.completed_batches + progress.failed_batches;
    if (processed === 0) {
      setEta('Calculating...');
      return;
    }

    const elapsed = (Date.now() - startTimeRef.current) / 1000;
    const avgPerBatch = elapsed / processed;
    const remaining = progress.total_batches - processed;
    const etaSeconds = Math.ceil(avgPerBatch * remaining);

    if (etaSeconds <= 0) {
      setEta('Almost done...');
    } else if (etaSeconds < 60) {
      setEta(`~${etaSeconds}s remaining`);
    } else {
      const mins = Math.ceil(etaSeconds / 60);
      setEta(`~${mins}min remaining`);
    }
  }, [progress.completed_batches, progress.failed_batches, progress.total_batches]);

  // Auto-transition when complete
  useEffect(() => {
    if (isComplete) {
      const timer = setTimeout(onComplete, 1500);
      return () => clearTimeout(timer);
    }
  }, [isComplete, onComplete]);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
            )}
            {isComplete ? 'AI Screening Complete' : 'AI Screening in Progress'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Progress bar */}
          <div className="space-y-2">
            <Progress value={percent} className="h-3" />
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>
                Wave {Math.min(
                  Math.ceil((progress.completed_batches + progress.failed_batches) / 10) + (isComplete ? 0 : 1),
                  Math.ceil(progress.total_batches / 10),
                )}/{Math.ceil(progress.total_batches / 10)}
                {' — '}
                {progress.completed_sources}/{progress.total_sources} sources
              </span>
              <span>{Math.round(percent)}%</span>
            </div>
            {!isComplete && (
              <p className="text-xs text-slate-400">{eta}</p>
            )}
          </div>

          {/* Live counters */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
              <CheckCircle2 className="mx-auto mb-1 h-5 w-5 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-700">{includeCount}</p>
              <p className="text-xs text-emerald-600">Include</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
              <HelpCircle className="mx-auto mb-1 h-5 w-5 text-amber-500" />
              <p className="text-2xl font-bold text-amber-700">{maybeCount}</p>
              <p className="text-xs text-amber-600">Maybe</p>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
              <XCircle className="mx-auto mb-1 h-5 w-5 text-rose-500" />
              <p className="text-2xl font-bold text-rose-700">{excludeCount}</p>
              <p className="text-xs text-rose-600">Exclude</p>
            </div>
          </div>

          {/* Failed batches warning */}
          {progress.failed_batches > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-700">
                {progress.failed_batches} batch{progress.failed_batches !== 1 ? 'es' : ''} failed.
                Results from successful batches are preserved.
              </p>
            </div>
          )}

          {/* Cancel button */}
          {!isComplete && (
            <Button variant="outline" onClick={onCancel} className="w-full">
              <X className="mr-2 h-4 w-4" />
              Cancel (keep current results)
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
